import { motion } from "framer-motion";
import { useRef } from "react";
import { useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1654658975918-a2c06424550a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxjb21tZXJjaWFsJTIwYnVpbGRpbmclMjBtb2Rlcm4lMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzg0MDU3MTgwfDA&ixlib=rb-4.1.0&q=85";

const LINES = ["We build", "the frames", "cities live in."];

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.5 + i * 0.14 },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen w-full overflow-hidden" data-testid="hero">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={HERO_IMG} alt="Modern commercial building" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/40 to-ink" />
      </motion.div>

      <div className="relative z-10 mx-auto max-w-[1600px] px-6 md:px-12 min-h-screen flex flex-col justify-end pb-16 pt-32">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="h-px w-12 bg-flame" />
          <span className="text-xs md:text-sm uppercase tracking-[0.35em] text-secondary-foreground/90">
            Commercial · Civil · Since 2009
          </span>
        </motion.div>

        <h1 className="font-heading font-black tracking-tighter text-[3.2rem] leading-[0.92] sm:text-7xl lg:text-[8.5rem] lg:leading-[0.85]">
          {LINES.map((line, i) => (
            <span key={i} className="line-mask">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
                className="block"
              >
                {i === 2 ? (
                  <>
                    cities <span className="text-flame">live</span> in.
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="mt-10 flex flex-col md:flex-row md:items-end justify-between gap-8"
        >
          <p className="max-w-md text-base md:text-lg text-secondary-foreground/85">
            KTR Constructions delivers commercial developments, civil infrastructure, renovations
            and interiors across London — engineered to last, finished to impress.
          </p>
          <a
            href="#projects"
            className="inline-flex items-center gap-3 text-sm uppercase tracking-widest text-white group"
            data-testid="hero-scroll-cta"
          >
            <span className="grid h-12 w-12 place-items-center border border-line group-hover:border-flame group-hover:bg-flame group-hover:text-ink transition-colors duration-300">
              <ArrowDown className="h-5 w-5" />
            </span>
            View our work
          </a>
        </motion.div>
      </div>
    </section>
  );
};
